import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  login: (username, password) => axios.post(`${API}/auth/login`, { username, password }),
  getMe: () => axios.get(`${API}/auth/me`, { headers: getAuthHeader() }),

  // Users
  getUsers: () => axios.get(`${API}/users`, { headers: getAuthHeader() }),
  createUser: (data) => axios.post(`${API}/users`, data, { headers: getAuthHeader() }),
  deleteUser: (username) => axios.delete(`${API}/users/${username}`, { headers: getAuthHeader() }),

  // Departments
  getDepartments: () => axios.get(`${API}/departments`),
  createDepartment: (data) => axios.post(`${API}/departments`, data, { headers: getAuthHeader() }),
  deleteDepartment: (id) => axios.delete(`${API}/departments/${id}`, { headers: getAuthHeader() }),

  // Employees
  getEmployees: () => axios.get(`${API}/employees`),
  getEmployeesByDepartment: (deptId) => axios.get(`${API}/employees/by-department/${deptId}`),
  createEmployee: (data) => axios.post(`${API}/employees`, data, { headers: getAuthHeader() }),
  updateEmployee: (id, data) => axios.put(`${API}/employees/${id}`, data, { headers: getAuthHeader() }),
  deleteEmployee: (id) => axios.delete(`${API}/employees/${id}`, { headers: getAuthHeader() }),

  // Visits
  getVisits: () => axios.get(`${API}/visits`, { headers: getAuthHeader() }),
  getVisit: (id) => axios.get(`${API}/visits/${id}`, { headers: getAuthHeader() }),
  createVisit: (data) => axios.post(`${API}/visits`, data),
  updateVisit: (id, data) => axios.put(`${API}/visits/${id}`, data, { headers: getAuthHeader() }),
  approveEntry: (id, approvedBy) => axios.post(`${API}/visits/${id}/approve-entry`, { approved_by: approvedBy }, { headers: getAuthHeader() }),
  approveExit: (id, approvedBy) => axios.post(`${API}/visits/${id}/approve-exit`, { approved_by: approvedBy }, { headers: getAuthHeader() }),
  getHistory: () => axios.get(`${API}/visits/history`, { headers: getAuthHeader() }),

  // ---------- FASE 1: ACADEMY ----------
  // Projects
  getProjects: () => axios.get(`${API}/projects`, { headers: getAuthHeader() }),
  createProject: (data) => axios.post(`${API}/projects`, data, { headers: getAuthHeader() }),
  deleteProject: (id) => axios.delete(`${API}/projects/${id}`, { headers: getAuthHeader() }),

  // Personal (usuarios rol "personal")
  getPersonal: (params = {}) => axios.get(`${API}/personal`, { headers: getAuthHeader(), params }),
  createPersonal: (data) => axios.post(`${API}/personal`, data, { headers: getAuthHeader() }),
  updatePersonal: (id, data) => axios.put(`${API}/personal/${id}`, data, { headers: getAuthHeader() }),
  resetPersonalPassword: (id) => axios.post(`${API}/personal/${id}/reset-password`, {}, { headers: getAuthHeader() }),
  deletePersonal: (id) => axios.delete(`${API}/personal/${id}`, { headers: getAuthHeader() }),
  generatePersonalCredentials: (fullName) => axios.get(`${API}/personal/generate-credentials`, { headers: getAuthHeader(), params: { full_name: fullName } }),
  downloadPersonalTemplate: () => axios.get(`${API}/personal/bulk-template`, { headers: getAuthHeader(), responseType: 'blob' }),
  bulkUploadPersonal: (formData) => axios.post(`${API}/personal/bulk-upload`, formData, { headers: getAuthHeader() }),

  // Courses
  getCourses: () => axios.get(`${API}/courses`, { headers: getAuthHeader() }),
  getCourse: (id) => axios.get(`${API}/courses/${id}`, { headers: getAuthHeader() }),
  createCourse: (data) => axios.post(`${API}/courses`, data, { headers: getAuthHeader() }),
  updateCourse: (id, data) => axios.put(`${API}/courses/${id}`, data, { headers: getAuthHeader() }),
  deleteCourse: (id) => axios.delete(`${API}/courses/${id}`, { headers: getAuthHeader() }),
  downloadQuestionsTemplate: () => axios.get(`${API}/courses/questions-template`, { headers: getAuthHeader(), responseType: 'blob' }),
  parseQuestionsExcel: (formData) => axios.post(`${API}/courses/questions-parse`, formData, { headers: getAuthHeader() }),
  startCourse: (id) => axios.post(`${API}/courses/${id}/start`, {}, { headers: getAuthHeader() }),
  updateCourseProgress: (id, currentSlide) => axios.put(`${API}/courses/${id}/progress`, { current_slide: currentSlide }, { headers: getAuthHeader() }),
  getEvaluation: (id) => axios.get(`${API}/courses/${id}/evaluation`, { headers: getAuthHeader() }),
  submitEvaluation: (id, answers) => axios.post(`${API}/courses/${id}/evaluate`, { course_id: id, answers }, { headers: getAuthHeader() }),

  // Diplomas
  upsertDiplomaTemplate: (data) => axios.post(`${API}/diplomas/templates`, data, { headers: getAuthHeader() }),
  getDiplomaTemplate: (courseId) => axios.get(`${API}/diplomas/templates/${courseId}`, { headers: getAuthHeader() }),
  getUserDiploma: (courseId) => axios.get(`${API}/diplomas/user/${courseId}`, { headers: getAuthHeader() }),

  // Training Reports
  getTrainingStats: () => axios.get(`${API}/reports/training/stats`, { headers: getAuthHeader() }),
  getTrainingReports: (params = {}) => axios.get(`${API}/reports/training`, { headers: getAuthHeader(), params }),
  getTrainingCharts: () => axios.get(`${API}/reports/training/charts`, { headers: getAuthHeader() }),
  downloadTrainingPDF: (params = {}) => axios.get(`${API}/reports/training-pdf`, { headers: getAuthHeader(), params, responseType: 'blob' }),

  // Course Assignments
  assignCourse: (courseId, userIds) => axios.post(`${API}/courses/${courseId}/assign`, { user_ids: userIds }, { headers: getAuthHeader() }),
  getCourseAssignments: (courseId) => axios.get(`${API}/courses/${courseId}/assignments`, { headers: getAuthHeader() }),
  unassignCourse: (courseId, userId) => axios.delete(`${API}/courses/${courseId}/assign/${userId}`, { headers: getAuthHeader() }),
  extendCourseAssignment: (courseId, userId, days) => axios.put(`${API}/courses/${courseId}/assign/${userId}/extend`, { days }, { headers: getAuthHeader() }),

  // Evaluation Attempts (admin analytics)
  listCourseAttempts: (courseId) => axios.get(`${API}/courses/${courseId}/attempts`, { headers: getAuthHeader() }),
  getAttemptDetail: (attemptId) => axios.get(`${API}/attempts/${attemptId}`, { headers: getAuthHeader() }),
  getCourseQuestionStats: (courseId) => axios.get(`${API}/courses/${courseId}/question-stats`, { headers: getAuthHeader() }),

  // System
  runReminders: () => axios.post(`${API}/system/run-reminders`, {}, { headers: getAuthHeader() }),
  cleanupOrphanProgress: () => axios.post(`${API}/system/cleanup-orphan-progress`, {}, { headers: getAuthHeader() }),

  // Intranet
  getIntranet: () => axios.get(`${API}/intranet`, { headers: getAuthHeader() }),
  createIntranet: (data) => axios.post(`${API}/intranet`, data, { headers: getAuthHeader() }),
  updateIntranet: (id, data) => axios.put(`${API}/intranet/${id}`, data, { headers: getAuthHeader() }),
  deleteIntranet: (id) => axios.delete(`${API}/intranet/${id}`, { headers: getAuthHeader() }),

  // Mis diplomas (personal)
  getMyDiplomas: () => axios.get(`${API}/me/diplomas`, { headers: getAuthHeader() }),

  // System / Email status
  getEmailStatus: () => axios.get(`${API}/system/email-status`, { headers: getAuthHeader() }),
};
